import { useEffect } from "react";

type Handlers = {
  onPrev?: () => void;
  onNext?: () => void;
  onPublish?: () => void;
  onPublishAndNext?: () => void;
  onToggleEdit?: () => void;
  onToggleScraper?: () => void;
  onShowHelp?: () => void;
  onDelete?: () => void;
  enabled?: boolean;
};

const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
};

export function useReviewKeyboard({
  onPrev,
  onNext,
  onPublish,
  onPublishAndNext,
  onToggleEdit,
  onToggleScraper,
  onShowHelp,
  onDelete,
  enabled = true,
}: Handlers) {
  useEffect(() => {
    if (!enabled) return;

    const handler = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;

      const key = event.key;
      switch (key) {
        case "j":
        case "ArrowRight":
          if (onNext) {
            event.preventDefault();
            onNext();
          }
          return;
        case "k":
        case "ArrowLeft":
          if (onPrev) {
            event.preventDefault();
            onPrev();
          }
          return;
        case "P":
          if (event.shiftKey && onPublishAndNext) {
            event.preventDefault();
            onPublishAndNext();
          }
          return;
        case "p":
          if (!event.shiftKey && onPublish) {
            event.preventDefault();
            onPublish();
          }
          return;
        case "e":
          if (onToggleEdit) {
            event.preventDefault();
            onToggleEdit();
          }
          return;
        case "s":
          if (onToggleScraper) {
            event.preventDefault();
            onToggleScraper();
          }
          return;
        case "?":
          if (onShowHelp) {
            event.preventDefault();
            onShowHelp();
          }
          return;
        case "d":
          if (onDelete) {
            event.preventDefault();
            onDelete();
          }
          return;
      }
    };

    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [
    enabled,
    onPrev,
    onNext,
    onPublish,
    onPublishAndNext,
    onToggleEdit,
    onToggleScraper,
    onShowHelp,
    onDelete,
  ]);
}
